'use client';

import { useResultChanged } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/draw';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import type { Definition } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { EventNumber } from '@svenskaspelab/poolbetting/schema/sport';
import { type PropsWithChildren, type ReactNode, useEffect, useRef } from 'react';

const RESULT_HIGHLIGHT_DURATION_MS = 2000;

type CouponRowWrapperProps = PropsWithChildren & {
  eventNumber: EventNumber;
  drawNumber: DrawNumber;
  product: Definition['drawProducts'][number];
};

/**
 * Wrapper component for coupon rows to handle result changes.
 *
 * @param root0 props for coupon row wrapper
 * @param root0.children React children elements
 * @param root0.eventNumber event number for coupon row
 * @param root0.drawNumber draw number for coupon row
 * @param root0.product product module engine
 * @returns Coupon row child components
 */
export default function CouponRowWrapper({
  children,
  eventNumber,
  drawNumber,
  product,
}: CouponRowWrapperProps): ReactNode {
  const hasResultChangedForEvent = useResultChanged(drawNumber, product).some(
    (changedResult) => changedResult.eventNumber === eventNumber
  );
  const refElement = useRef<HTMLDivElement | null>(null);
  const resetHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearHighlight = (element: HTMLDivElement): void => {
    element.classList.remove('delay-none', 'bg-success-variant');
    element.classList.add('delay-1800');
  };

  const clearPendingHighlightReset = (): void => {
    if (resetHighlightTimerRef.current !== undefined) {
      clearTimeout(resetHighlightTimerRef.current);
      resetHighlightTimerRef.current = undefined;
    }
  };

  useEffect(() => {
    const element = refElement.current;

    if (!element) {
      return undefined;
    }

    if (hasResultChangedForEvent) {
      clearPendingHighlightReset();

      // Reset first so repeated updates for the same event can restart the highlight.
      clearHighlight(element);

      element.getBoundingClientRect();

      element.classList.remove('delay-1800');
      element.classList.add('delay-none', 'bg-success-variant');

      resetHighlightTimerRef.current = setTimeout(() => {
        clearHighlight(element);
        resetHighlightTimerRef.current = undefined;
      }, RESULT_HIGHLIGHT_DURATION_MS);
    }

    return undefined;
  }, [hasResultChangedForEvent]);

  useEffect(() => {
    return (): void => {
      clearPendingHighlightReset();
    };
  }, []);

  return (
    <div ref={refElement} className="transition-colors duration-1000 ease-in-out">
      {children}
    </div>
  );
}
